import { AppLayout } from '@/components/layout/AppLayout';
import { AdminStatCard } from '@/components/admin/AdminStatCard';
import { LatencyChart } from '@/components/admin/LatencyChart';
import { SlaBar } from '@/components/admin/SlaBar';
import { ServiceHealthList } from '@/components/admin/ServiceHealthList';
import { TrafficChart } from '@/components/admin/TrafficChart';
import { Badge } from '@/components/ui/badge';
import { GlassCard } from '@/components/ui/layout/card';
import {
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@/components/ui/layout/card';
import { CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { useOpsSummary } from '@/hooks/useOpsSummary';

/**
 * Internal Admin Dashboard
 * Read-only ops dashboard for monitoring system health, latency, and reliability.
 * Shows empty state when API data is missing/failed.
 */
export default function AdminDashboard() {
  const { data: backendData, isLoading, error } = useOpsSummary(true);

  const viewData = (() => {
    if (!backendData) {
      return null;
    }
    return {
      apiHealth: backendData.api_health === 'ok' ? ('healthy' as const) : ('degraded' as const),
      mcpStatus: backendData.mcp_status === 'ok' ? ('online' as const) : ('degraded' as const),
      uptime: backendData.uptime,
      errorRate: backendData.error_rate,
      latency: {
        p50: backendData.latency.p50,
        p95: backendData.latency.p95,
        p99: backendData.latency.p99,
      },
      latencyHistory: backendData.latency_history.map((h) => ({
        time: h.time,
        p50: h.p50,
        p95: h.p95,
        p99: h.p99,
      })),
      sla: backendData.sla,
      slaThreshold: backendData.sla_threshold,
      services: backendData.services.map((s) => ({
        name: s.name,
        p95: s.p95_ms,
        status: s.status === 'ok' ? ('ok' as const) : s.status === 'warn' ? ('warn' as const) : ('critical' as const),
      })),
      reliabilitySignals: backendData.reliability_signals ?? [],
      traffic: {
        activeUsers15m: backendData.traffic.active_users_15m,
        activeUsers24h: backendData.traffic.active_users_24h,
        concurrentSessions: backendData.traffic.concurrent_sessions,
        requestsPerMinute: backendData.traffic.requests_per_minute,
        executorRunsPerMinute: backendData.traffic.executor_runs_per_minute,
        planBuildsPerHour: backendData.traffic.plan_builds_per_hour,
        toolCallsPerMinute: backendData.traffic.tool_calls_per_minute,
        requestVolumeHistory: backendData.traffic.request_volume_history ?? [],
        activitySignals: backendData.activity_signals ?? [],
      },
    };
  })();

  if (isLoading) {
    return (
      <AppLayout>
        <div className="text-muted-foreground">Loading system metrics…</div>
      </AppLayout>
    );
  }

  if (error || !viewData) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold text-primary">Internal Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">System health, latency, and reliability</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-8 text-center text-muted-foreground">
            <p className="font-medium">Data unavailable</p>
            <p className="text-sm mt-1">Connect to the API to view ops metrics.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-primary">Internal Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              System health, latency, and reliability
            </p>
          </div>
          <Badge variant="outline" className="self-start sm:self-auto text-muted-foreground border-muted-foreground/30">
            Internal · Read-only
          </Badge>
        </div>

        {/* System Status Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <AdminStatCard
            label="API"
            value={viewData.apiHealth === 'healthy' ? 'Healthy' : 'Degraded'}
            status={viewData.apiHealth === 'healthy' ? 'healthy' : 'critical'}
          />
          <AdminStatCard
            label="MCP"
            value={viewData.mcpStatus === 'online' ? 'Online' : 'Degraded'}
            subtext="DB / FS"
            status={viewData.mcpStatus === 'online' ? 'healthy' : 'critical'}
          />
          <AdminStatCard
            label="Error Rate"
            value={`${viewData.errorRate}%`}
            subtext="Last 24h"
            status={viewData.errorRate < 1 ? 'healthy' : viewData.errorRate < 5 ? 'warning' : 'critical'}
          />
          <AdminStatCard
            label="Uptime"
            value={`${viewData.uptime}%`}
            subtext="30 days"
            status={viewData.uptime >= 99.9 ? 'healthy' : viewData.uptime >= 99 ? 'warning' : 'critical'}
          />
        </div>

        {/* Traffic Overview */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-foreground">Traffic Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <AdminStatCard
              label="Active Users (15m)"
              value={String(viewData.traffic.activeUsers15m)}
              subtext="Last 15 minutes"
              status="healthy"
            />
            <AdminStatCard
              label="Active Users (24h)"
              value={String(viewData.traffic.activeUsers24h)}
              subtext="Last 24 hours"
              status="healthy"
            />
            <AdminStatCard
              label="Concurrent Sessions"
              value={String(viewData.traffic.concurrentSessions)}
              subtext="Right now"
              status="healthy"
            />
            <AdminStatCard
              label="Requests / Minute"
              value={String(viewData.traffic.requestsPerMinute)}
              subtext="Avg last 5 min"
              status="healthy"
            />
          </div>
        </div>

        {/* Request Volume Chart */}
        <TrafficChart data={viewData.traffic.requestVolumeHistory} />

        {/* Execution Activity */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-foreground">Execution Activity</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <AdminStatCard
              label="Executor Runs / Min"
              value={String(viewData.traffic.executorRunsPerMinute)}
              subtext="Avg last 10 min"
              status="healthy"
            />
            <AdminStatCard
              label="Plan Builds / Hour"
              value={String(viewData.traffic.planBuildsPerHour)}
              subtext="Last hour"
              status="healthy"
            />
            <AdminStatCard
              label="Tool Calls / Min"
              value={String(viewData.traffic.toolCallsPerMinute)}
              subtext="All MCP tools"
              status="healthy"
            />
          </div>
        </div>

        {/* Activity Signals */}
        <GlassCard>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Activity Signals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {viewData.traffic.activitySignals.map((signal, index) => (
              <div
                key={index}
                className="flex items-center gap-3 py-2 px-3 rounded-md bg-muted/30"
              >
                {signal.type === 'success' && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                )}
                {signal.type === 'warning' && (
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                )}
                {signal.type === 'info' && (
                  <Info className="h-4 w-4 text-blue-500 shrink-0" />
                )}
                <span className="text-sm text-foreground">{signal.message}</span>
              </div>
            ))}
          </CardContent>
        </GlassCard>

        {/* Latency & SLA Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-medium text-foreground">Latency & Reliability</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LatencyChart data={viewData.latencyHistory} currentMetrics={viewData.latency} />
            <div className="space-y-4">
              <SlaBar value={viewData.sla} threshold={viewData.slaThreshold} />
              <ServiceHealthList services={viewData.services} />
            </div>
          </div>
        </div>

        {/* Reliability Signals */}
        <GlassCard>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Reliability Signals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {viewData.reliabilitySignals.map((signal, index) => (
              <div
                key={index}
                className="flex items-center gap-3 py-2 px-3 rounded-md bg-muted/30"
              >
                {signal.type === 'success' && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                )}
                {signal.type === 'warning' && (
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                )}
                {signal.type === 'info' && (
                  <Info className="h-4 w-4 text-blue-500 shrink-0" />
                )}
                <span className="text-sm text-foreground">{signal.message}</span>
              </div>
            ))}
          </CardContent>
        </GlassCard>
      </div>
    </AppLayout>
  );
}
